import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  IFeatureInfo,
  IFeatureType,
  IPlanFeatureInfo,
  ISubscriptionPlanInfo,
  ISubscriptionPlanWithTaxAndDiscountInfo,
  PlanButtonLabel,
  SubscriptionType,
} from '../../../models/Subscription';
import {
  formatAmount,
  getCurrencySymbol,
  isOneTimeBillingCycle,
} from '../../../_helpers/common.functions';

// Function that returns dummy features list for checking
export const getDummyFeatures = (count: number = 5): IFeatureInfo[] => {
  const dummyFeatures: IFeatureInfo[] = [
    {
      Id: 'dummy-1',
      Name: 'Unlock new book with full-access every month',
      Description: 'Unlock new book with full-access every month',
      FeatureType: IFeatureType.ProductFeature,
    },
    {
      Id: 'dummy-2',
      Name: 'Audio stories, poems, and conversations',
      Description: 'Audio stories, poems, and conversations',
      FeatureType: IFeatureType.ProductFeature,
    },
    {
      Id: 'dummy-3',
      Name: 'Pre-translated Text and Audio in native language (Hindi)',
      Description: 'Pre-translated Text and Audio in native language (Hindi)',
      FeatureType: IFeatureType.ProductFeature,
    },
    {
      Id: 'dummy-4',
      Name: 'Reading program (Paid add-on — included free for a limited time)',
      Description:
        'Reading program (Paid add-on — included free for a limited time)',
      FeatureType: IFeatureType.ProductFeature,
    },
  ];

  return dummyFeatures;
};

export const getCardInfo = (data: ISubscriptionPlanWithTaxAndDiscountInfo) => {
  const isFreePlan = data.SubscriptionType === SubscriptionType.Free;
  const isBlocked =
    Array.isArray(data.AvailableActions) && data.AvailableActions.length === 0;
  const billingCycle = data.BillingCycle.toString();
  const planLabel = `${data.Name} Plan`; // Show Only Name
  const buttonLabel = getPlanCardButtonLabel(data);

  const isOneTime = isOneTimeBillingCycle(billingCycle);
  const priceSuffix = isOneTime
    ? '(One-Time)'
    : billingCycle === 'Yearly'
    ? '/Year'
    : '/Month';

  const showFinalAmountWithoutDiscount =
    data.FinalAmountWithoutDiscount - data.FinalAmount > 0;

  // NEW: only override the default subtext when status is a non-"Active" halted/cancelled/paused/expired state
  const statusOverride =
    data.AutoRenewalStatus && data.AutoRenewalStatus !== 'Active'
      ? getAutoRenewalStatusLabel(data.AutoRenewalStatus, data.EndDate)
      : null;

  return {
    PlanLabel: planLabel,
    ButtonLabel: buttonLabel,
    Price: `${getCurrencySymbol(data.Currency)}${formatAmount(
      data.FinalAmount,
      data.Currency,
      true,
    )}`,
    FinalPriceWithoutDiscount: `${getCurrencySymbol(
      data.Currency,
    )}${formatAmount(data.FinalAmountWithoutDiscount, data.Currency, true)}`,
    PriceLabel: priceSuffix,
    Features: getUniqueFeatures(data),
    showButton: !isFreePlan && !isBlocked,
    showFinalAmountWithoutDiscount:
      showFinalAmountWithoutDiscount && !isFreePlan,
    PlanAction:
      isFreePlan || isBlocked
        ? null
        : statusOverride ?? getPlanAction(buttonLabel), // CHANGED
  };
};
export const getDiscount = (
  data: ISubscriptionPlanInfo,
): { text: string } | null => {
  // Check the Features array for DiscountPercent or DiscountAmount
  const discountFeature = data.Features.find(
    (feature) =>
      feature.FeatureType === IFeatureType.DiscountPercent ||
      feature.FeatureType === IFeatureType.DiscountAmount,
  );

  // If no discount feature is found, return null
  if (!discountFeature || !discountFeature.Value) {
    return null;
  }

  // Handle DiscountPercent
  if (discountFeature.FeatureType === IFeatureType.DiscountPercent) {
    return { text: `Save ${discountFeature.Value}%` };
  }

  // Handle DiscountAmount
  if (discountFeature.FeatureType === IFeatureType.DiscountAmount) {
    return {
      text: `Save ${getCurrencySymbol(data.Currency)} ${discountFeature.Value}`,
    };
  }

  return null; // Ensure all code paths return a value
};

export const getUniqueFeatures = (
  data: ISubscriptionPlanInfo,
): IPlanFeatureInfo[] => {
  const uniqueMap = new Map<string, IPlanFeatureInfo>();

  data.Features.forEach((feature) => {
    // Only Pick the Feature Type "General"
    if (
      feature.FeatureType === IFeatureType.General &&
      !uniqueMap.has(feature.FeatureId)
    ) {
      uniqueMap.set(feature.FeatureId, feature);
    }
  });

  // Sort by the display order set on the plan; fall back to Created At for ties
  return Array.from(uniqueMap.values()).sort(
    (a, b) =>
      (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0) ||
      new Date(a.CreatedAt).getTime() - new Date(b.CreatedAt).getTime(),
  );
};

export const getAutoRenewalStatusLabel = (
  status?: string | null,
  endDate?: string | null,
): string | null => {
  if (!status) return null;

  // Cancelled but not yet ended: the backend only sends "Cancelled" while access remains
  // (an ended one comes as "Expired"), so say until when.
  const accessUntil = formatPlanEndDate(endDate);
  if (status === 'Cancelled' && accessUntil) {
    return `Auto-renewal cancelled — access until ${accessUntil}`;
  }

  const map: Record<string, string> = {
    Suspended: 'Payment failed — please renew to continue',
    Cancelled: 'Auto-renewal was cancelled',
    Paused: 'Subscription is paused',
    Expired: 'Subscription has ended',
  };
  return map[status] ?? null;
};

// The API sends UTC dates without a zone; null when there is no usable date.
const formatPlanEndDate = (endDate?: string | null): string | null => {
  if (!endDate) return null;
  const date = new Date(endDate.endsWith('Z') ? endDate : endDate + 'Z');
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const getPlanCardButtonLabel = (
  plan: ISubscriptionPlanWithTaxAndDiscountInfo,
): string => {
  const action = plan.AvailableActions?.[0] ?? 'Subscribe';

  const labelMap: Record<string, PlanButtonLabel> = {
    Subscribe: PlanButtonLabel.Subscribe,
    Change: PlanButtonLabel.Change,
    Renew: PlanButtonLabel.Renew,
    Complete: PlanButtonLabel.Complete,
    Active: PlanButtonLabel.Active,
    Cancel: PlanButtonLabel.Cancel,
  };

  const label = labelMap[action] ?? PlanButtonLabel.Subscribe;

  // One-time plans (e.g. Bundle) are purchased, not subscribed to
  return label === PlanButtonLabel.Subscribe &&
    isOneTimeBillingCycle(plan.BillingCycle)
    ? PlanButtonLabel.Buy
    : label;
};

export const getPlanAction = (buttonLabel: string): string | null => {
  const subtextMap: Record<string, string | null> = {
    [PlanButtonLabel.Subscribe]: 'Auto-renews every month until you cancel',
    [PlanButtonLabel.Buy]: 'Purchase once for full access to all books',
    [PlanButtonLabel.Change]: null,
    [PlanButtonLabel.Renew]: 'Renew to keep reading without interruption',
    [PlanButtonLabel.Complete]: 'Complete payment to resume reading',
    [PlanButtonLabel.OrderPending]:
      'An active order is awaiting payment confirmation. Please check again shortly',
    [PlanButtonLabel.Active]: 'This is your current plan',
    [PlanButtonLabel.Cancel]: 'Cancel auto-renewal for this plan',
  };

  return subtextMap[buttonLabel] ?? null;
};

@Component({
  selector: 'app-subscription-plan-card',
  standalone: false,
  templateUrl: './subscription-plan-card.component.html',
  styleUrl: './subscription-plan-card.component.scss',
})
export class SubscriptionPlanCardComponent implements OnChanges {
  @Input() subscriptionPlan!: ISubscriptionPlanWithTaxAndDiscountInfo;
  @Output() subscriptionPlanClick = new EventEmitter<{
    plan: ISubscriptionPlanWithTaxAndDiscountInfo;
    action: string;
  }>();
  @Input() hasPendingOrder = false;
  /** Hides the action button and its note - the user's organization manages their plan. */
  @Input() managedByOrganization = false;
  // Days left in the user's free trial; null when the user is not in a trial
  @Input() trialDaysRemaining: number | null = null;
  PlanButtonLabel = PlanButtonLabel;
  getPlanAction = getPlanAction;

  discountPercentage: { value: number; text: string };
  getPlanCardButtonLabel = getPlanCardButtonLabel;
  getUniqueFeatures = getUniqueFeatures;
  getCardInfo = getCardInfo;
  getDiscount = getDiscount;

  constructor() {
    this.discountPercentage = this.getDiscountPercentage(this.subscriptionPlan);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.discountPercentage = this.getDiscountPercentage(this.subscriptionPlan);
  }

  // An auto-renewing plan the backend withheld (no actions) because the free trial is running
  get isBlockedByTrial(): boolean {
    const plan = this.subscriptionPlan;
    return (
      this.trialDaysRemaining !== null &&
      this.trialDaysRemaining > 0 &&
      plan?.SubscriptionType !== SubscriptionType.Free &&
      plan?.IsAutoRenewing === true &&
      Array.isArray(plan.AvailableActions) &&
      plan.AvailableActions.length === 0
    );
  }

  get trialMessage(): string {
    const days = this.trialDaysRemaining ?? 0;
    console.log(this.trialDaysRemaining);
    return `Available for subscribe after free trial expires in ${days} ${
      days === 1 ? 'day' : 'days'
    }.`;
  }

  getDiscountPercentage(plan: ISubscriptionPlanInfo) {
    let originalPrice = plan?.FinalAmountWithoutDiscount;
    let discountedPrice = plan?.FinalAmount;

    if (!originalPrice || originalPrice <= 0) {
      return { value: 0, text: '' };
    }

    const percentage = Math.round(
      ((originalPrice - discountedPrice) / originalPrice) * 100,
    );

    return {
      value: Math.max(0, percentage),
      text: `${Math.max(0, percentage)}%`,
    };
  }

  onSubscriptionPlanClick(
    plan: ISubscriptionPlanWithTaxAndDiscountInfo,
    action: string,
  ): void {
    this.subscriptionPlanClick.emit({ plan, action });
  }
}
